import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Clock, ArrowRight, BookOpen, ArrowLeft, User } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Button } from "@/components/ui/button-variants";
import { supabase } from "@/integrations/supabase/client";
import { LazyImage } from "@/components/ui/lazy-image";

interface Article {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  author: string;
  category: string;
  reading_time: number;
  featured_image: string | null;
}

const BlogSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [carouselApi, setCarouselApi] = useState<any>(null);
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      const { data, error } = await supabase
        .from('blog_articles')
        .select('id, slug, title, excerpt, author, category, reading_time, featured_image')
        .eq('is_active', true)
        .lte('publication_date', new Date().toISOString())
        .order('publication_date', { ascending: false })
        .limit(3);

      if (error) throw error;
      setArticles(data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des articles:', error);
    } finally {
      setLoading(false);
    }
  };

  if (carouselApi) {
    carouselApi.on('select', () => {
      setCurrentSlide(carouselApi.selectedScrollSnap());
    });
  }

  if (loading) {
    return (
      <section className="py-20 md:py-28 bg-gradient-to-br from-muted/20 via-white to-muted/30">
        <div className="container mx-auto px-4 text-center">
          <p className="text-muted-foreground">Chargement des articles...</p>
        </div>
      </section>
    );
  }

  if (articles.length === 0) {
    return null;
  }

  return (
    <section className="py-20 md:py-28 bg-gradient-to-br from-muted/20 via-white to-muted/30 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:60px_60px]"></div>
      <div className="absolute top-1/2 left-0 w-96 h-96 bg-gradient-to-r from-primary/10 to-transparent rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-l from-secondary/10 to-transparent rounded-full blur-3xl"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="flex flex-col md:flex-row items-center justify-between mb-16 gap-6">
          <div>
            <div className="inline-flex items-center gap-2 px-5 py-2 bg-white/80 backdrop-blur-sm rounded-full mb-4 border border-primary/10 shadow-lg">
              <BookOpen className="w-4 h-4 text-primary" />
              <span className="text-primary font-semibold text-sm tracking-wide">ACTUALITÉS</span>
            </div>
            <h2 className="font-poppins font-black text-3xl md:text-5xl bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              Dernières nouvelles
            </h2>
            <p className="text-muted-foreground text-lg mt-2">
              Conseils, témoignages et guides pour votre carrière
            </p>
          </div>
          <Button 
            variant="outline" 
            size="lg"
            className="border-2 border-primary/30 bg-white/60 backdrop-blur-sm hover:bg-primary hover:text-white hover:border-primary shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 group"
            asChild
          >
            <Link to="/blog" className="font-semibold">
              Voir tous les articles
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Link>
          </Button>
        </div>


        {/* Mobile Carousel */}
        <div className="md:hidden">
          <Carousel 
            className="w-full max-w-sm mx-auto"
            setApi={setCarouselApi}
            opts={{
              align: "center",
              loop: true,
            }}
          >
            <CarouselContent>
              {articles.map((article, index) => (
                <CarouselItem key={article.id}>
                  <Link
                    to={`/blog/${article.slug}`}
                    className="group relative block p-1"
                  >
                    <div className="absolute -inset-0.5 bg-gradient-to-br from-secondary to-secondary/80 rounded-3xl opacity-0 group-hover:opacity-30 blur-xl transition-opacity duration-500"></div>
                    
                    <div className="relative bg-white/90 backdrop-blur-sm rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden border border-white/50 group-hover:border-primary/30 h-full flex flex-col">
                      <div className="relative h-56 overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent z-10"></div>
                        {article.featured_image && (
                          <LazyImage 
                            src={article.featured_image} 
                            alt={article.title}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                          />
                        )}
                        <div className="absolute top-4 left-4 z-20">
                          <span className="px-3 py-1 bg-white/90 backdrop-blur-sm text-primary text-xs font-semibold rounded-full">
                            {article.category}
                          </span>
                        </div>
                      </div>

                      <div className="p-8 flex-1 flex flex-col">
                        <h3 className="font-poppins font-bold text-xl text-foreground mb-4 group-hover:text-primary transition-colors line-clamp-2 leading-tight">
                          {article.title}
                        </h3>
                        <p className="text-muted-foreground text-sm mb-6 line-clamp-3 leading-relaxed flex-1">
                          {article.excerpt}
                        </p>
                        
                        <div className="flex items-center justify-between pt-4 border-t border-border/50">
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2">
                              <User className="w-3.5 h-3.5 text-muted-foreground" />
                              <span className="text-xs text-muted-foreground">{article.author}</span>
                            </div>
                            <span className="text-muted-foreground">•</span>
                            <div className="flex items-center gap-1.5">
                              <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                              <span className="text-xs text-muted-foreground">{article.reading_time} min</span>
                            </div>
                          </div>
                          <div className="w-8 h-8 bg-gradient-to-br from-secondary to-secondary/80 rounded-full flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-300">
                            <ArrowRight className="w-4 h-4 text-white" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                </CarouselItem>
              ))}
            </CarouselContent>
            
            <CarouselPrevious className="left-2 h-10 w-10 bg-white/90 backdrop-blur-sm text-primary hover:bg-primary hover:text-white shadow-lg border border-primary/20">
              <ArrowLeft className="h-5 w-5" />
            </CarouselPrevious>
            <CarouselNext className="right-2 h-10 w-10 bg-white/90 backdrop-blur-sm text-primary hover:bg-primary hover:text-white shadow-lg border border-primary/20">
              <ArrowRight className="h-5 w-5" />
            </CarouselNext>
          </Carousel>
          
          <div className="flex justify-center gap-3 mt-6">
            {articles.map((_, index) => (
              <button
                key={index}
                onClick={() => carouselApi?.scrollTo(index)}
                className={`p-2 rounded-full transition-all duration-300 ${
                  currentSlide === index 
                    ? 'bg-primary' 
                    : 'bg-transparent'
                }`}
                aria-label={`Aller à la slide ${index + 1}`}
              >
                <div className={`h-2 w-2 rounded-full transition-all duration-300 ${
                  currentSlide === index 
                    ? 'bg-white scale-125' 
                    : 'bg-muted-foreground/30'
                }`} />
              </button>
            ))}
          </div>
        </div>

        {/* Desktop Grid */}
        <div className="hidden md:grid md:grid-cols-3 gap-8">
          {articles.map((article, index) => (
            <Link
              key={article.id}
              to={`/blog/${article.slug}`}
              className="group relative animate-fade-in"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Glow effect */}
              <div className="absolute -inset-0.5 bg-gradient-to-br from-secondary to-secondary/80 rounded-3xl opacity-0 group-hover:opacity-30 blur-xl transition-opacity duration-500"></div>
              
              {/* Card */}
              <div className="relative bg-white/90 backdrop-blur-sm rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden border border-white/50 group-hover:border-primary/30 h-full flex flex-col group-hover:scale-[1.02]">
                {/* Article Image */}
                <div className="relative h-56 overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent z-10"></div>
                  {article.featured_image && (
                    <LazyImage 
                      src={article.featured_image} 
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                  )}
                  <div className="absolute top-4 left-4 z-20">
                    <span className="px-3 py-1 bg-white/90 backdrop-blur-sm text-primary text-xs font-semibold rounded-full">
                      {article.category}
                    </span>
                  </div>
                </div>

                <div className="p-8 flex-1 flex flex-col">
                  <h3 className="font-poppins font-bold text-xl text-foreground mb-4 group-hover:text-primary transition-colors line-clamp-2 leading-tight">
                    {article.title}
                  </h3>
                  <p className="text-muted-foreground text-sm mb-6 line-clamp-3 leading-relaxed flex-1">
                    {article.excerpt}
                  </p>
                  
                  <div className="flex items-center justify-between pt-4 border-t border-border/50">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <User className="w-3.5 h-3.5 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">{article.author}</span>
                      </div>
                      <span className="text-muted-foreground">•</span>
                      <div className="flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">{article.reading_time} min</span>
                      </div>
                    </div>
                    <div className="w-8 h-8 bg-gradient-to-br from-secondary to-secondary/80 rounded-full flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-300">
                      <ArrowRight className="w-4 h-4 text-white group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BlogSection;